import { fmtCount, fmtSCU } from '@itc-mod/supply/shared';
import { z } from 'zod';
import { isPlaceholder } from './rules.ts';
import type { Crew, Op } from './schemas.ts';

// People and the contribution scorecard (plan 2D R12–R14, after `attendance`,
// `scoreRows` and `peopleRows`, sierra-panel.html lines 710, 1459 and 1292). Worked out
// when read, from crew, donations, deposits and refinery runs. People are keyed
// `user:<id>` or `name:<lower-cased name>`, as Donations' boards are.

const DAY_MS = 24 * 60 * 60 * 1000;

export const ScorePeriod = z.enum(['30', '90', 'all']);
export type ScorePeriod = z.infer<typeof ScorePeriod>;

export const periodDays = (period: ScorePeriod): number | null => (period === 'all' ? null : Number(period));

/** When a period starts, or null for all time. */
export function periodStart(period: ScorePeriod, now: Date): string | null {
  const days = periodDays(period);
  return days === null ? null : new Date(now.getTime() - days * DAY_MS).toISOString();
}

type OpFacts = Pick<Op, 'kind' | 'status' | 'outcome' | 'start' | 'updated'>;

/** An op's time: its start, or its last change. */
export const opTime = (op: Pick<Op, 'start' | 'updated'>) => Date.parse(op.start ?? op.updated);

/**
 * R12: what crew on this op count for. *Port:* a future sign-up isn't attendance, and
 * nor is a Scrubbed op. A battle op has no crew.
 */
export function attendance(op: OpFacts, now: Date): { signed: boolean; showed: boolean } {
  if (op.kind === 'battle') return { signed: false, showed: false };
  if (op.outcome === 'Scrubbed') return { signed: true, showed: false };
  if (op.status === 'complete') return { signed: true, showed: true };
  return { signed: true, showed: op.start !== null && Date.parse(op.start) <= now.getTime() };
}

/** R7: what one crew row counts for. An RSVP is a sign-up, and never shows, so it earns no op and no haul. */
export function rowAttendance(op: OpFacts, source: Crew['source'], now: Date): { signed: boolean; showed: boolean } {
  const { signed, showed } = attendance(op, now);
  return { signed, showed: showed && source !== 'rsvp' };
}

/** A person: a member, or a guest's name. Null for a placeholder or "Anonymous", who are never counted. */
export function personKey(user: string | null, name: string | null): string | null {
  if (user !== null) return `user:${user}`;
  const text = (name ?? '').trim();
  return isPlaceholder(text) || text.toLowerCase() === 'anonymous' ? null : `name:${text.toLowerCase()}`;
}

/** One op with its crew and its whole haul, in thousandths of an SCU. */
export interface OpCrewFacts {
  op: OpFacts;
  crew: readonly Pick<Crew, 'user' | 'guest' | 'source'>[];
  haulScu: number;
}

export interface AttendanceRow {
  key: string;
  user: string | null;
  name: string;
  signed: number;
  showed: number;
  /** The last op they showed up to, or null. */
  last: string | null;
  /** The last op they signed up for or showed up to, or null. */
  seen: string | null;
}

const later = (a: string | null, b: string) => (a === null || b > a ? b : a);
const nameOf = (user: string | null, guest: string | null, people: Readonly<Record<string, string>>) => guest ?? (user === null ? 'Unnamed' : (people[user] ?? 'a member'));

/** R12, summed per person over the last `days`, or all time. */
export function attendanceRows(ops: readonly OpCrewFacts[], people: Readonly<Record<string, string>>, days: number | null, now: Date): AttendanceRow[] {
  const since = days === null ? Number.NEGATIVE_INFINITY : now.getTime() - days * DAY_MS;
  const rows = new Map<string, AttendanceRow>();
  for (const { op, crew } of ops) {
    if (opTime(op) < since) continue;
    if (!attendance(op, now).signed) continue;
    const at = new Date(opTime(op)).toISOString();
    for (const { user, guest, source } of crew) {
      const key = personKey(user, guest);
      if (key === null) continue;
      const { showed } = rowAttendance(op, source, now);
      const row = rows.get(key) ?? { key, user, name: nameOf(user, guest, people), signed: 0, showed: 0, last: null, seen: null };
      row.signed += 1;
      row.seen = later(row.seen, at);
      if (showed) {
        row.showed += 1;
        row.last = later(row.last, at);
      }
      rows.set(key, row);
    }
  }
  return [...rows.values()];
}

export interface ParticipationRow {
  key: string;
  user: string | null;
  name: string;
  ops: number;
  seconds: number;
}

/**
 * Plan 3B R7: how many ops someone has time on, and how much, over the last `days`, or all
 * time. It reads crew rows, so battle ops count, and time an op shows for someone with no
 * row doesn't. A Scrubbed op credits nobody, even a row that had time before it was scrubbed.
 */
export function participation(ops: readonly { op: Pick<Op, 'start' | 'updated' | 'outcome'>; crew: readonly Pick<Crew, 'user' | 'guest' | 'presentSeconds'>[] }[], people: Readonly<Record<string, string>>, days: number | null, now: Date): ParticipationRow[] {
  const since = days === null ? null : now.getTime() - days * DAY_MS;
  const rows = new Map<string, ParticipationRow>();
  for (const { op, crew } of ops) {
    if (op.outcome === 'Scrubbed' || (since !== null && opTime(op) < since)) continue;
    for (const { user, guest, presentSeconds } of crew) {
      const key = personKey(user, guest);
      if (key === null || presentSeconds === 0) continue;
      const row = rows.get(key) ?? { key, user, name: nameOf(user, guest, people), ops: 0, seconds: 0 };
      row.ops += 1;
      row.seconds += presentSeconds;
      rows.set(key, row);
    }
  }
  return [...rows.values()];
}

/** A credited donor, as Donations' `contributions` gives them. */
export interface DonorFacts {
  userId: string | null;
  name: string;
  count: number;
  scu: number;
  auec: number;
  hidden: boolean;
  last: string;
}

/** Deposits made in the bank, as `donorDeposits` gives them. */
export interface DepositFacts {
  userId: string | null;
  name: string | null;
  count: number;
  amount: number;
}

export interface ScoreRow {
  key: string;
  user: string | null;
  name: string;
  ops: number;
  /** Each op's whole haul, credited to everyone who showed. */
  haul: number;
  donations: number;
  donatedScu: number;
  donatedAuec: number;
  runs: number;
  refinedScu: number;
  total: number;
  /** Opted out of the public boards: marked on the page, and their donation figures left out of the copied text. */
  hidden: boolean;
}

const plural = (count: number, word: string) => `${String(count)} ${word}${count === 1 ? '' : 's'}`;

/** What someone gave, as words, for the scorecard's text and its cards. */
export function scoreBits(row: ScoreRow): string[] {
  const given = [row.donatedScu > 0 ? `${fmtSCU(row.donatedScu)} SCU` : '', row.donatedAuec > 0 ? `${fmtCount(row.donatedAuec)} aUEC` : ''].filter((part) => part !== '');
  return [
    row.ops > 0 ? plural(row.ops, 'op') : '',
    row.donations > 0 ? `${plural(row.donations, 'donation')}${given.length > 0 ? ` (${given.join(', ')})` : ''}` : '',
    row.runs > 0 ? `${plural(row.runs, 'refinery run')}${row.refinedScu > 0 ? ` (${fmtSCU(row.refinedScu)} SCU)` : ''}` : '',
    row.haul > 0 ? `${fmtSCU(row.haul)} SCU hauled on ops` : '',
  ].filter((part) => part !== '');
}

/**
 * The scorecard as it may leave the app (plan 2E R16), for its text and its cards, so
 * they can't drift: someone who opted out has their donation figures left out, and goes
 * if nothing else remains. They rank on what's left, so the order gives nothing away.
 */
export function publicScore(rows: readonly ScoreRow[]): ScoreRow[] {
  return rows
    .map((row) => (row.hidden ? { ...row, donations: 0, donatedScu: 0, donatedAuec: 0, total: row.ops + row.runs } : row))
    .filter((row) => row.total > 0)
    .sort((a, b) => b.total - a.total || b.haul - a.haul || b.donatedScu - a.donatedScu || a.name.localeCompare(b.name));
}

/** R13. */
export function scoreRows(input: { ops: readonly OpCrewFacts[]; donors: readonly DonorFacts[]; deposits: readonly DepositFacts[]; runs: readonly { refiner: string; count: number; scu: number }[]; hidden?: ReadonlySet<string>; people: Readonly<Record<string, string>>; days: number | null; now: Date }): ScoreRow[] {
  const rows = new Map<string, ScoreRow>();
  const row = (user: string | null, name: string | null): ScoreRow | null => {
    const key = personKey(user, name);
    if (key === null) return null;
    const found = rows.get(key) ?? { key, user, name: nameOf(user, name, input.people), ops: 0, haul: 0, donations: 0, donatedScu: 0, donatedAuec: 0, runs: 0, refinedScu: 0, total: 0, hidden: false };
    rows.set(key, found);
    return found;
  };
  const since = input.days === null ? Number.NEGATIVE_INFINITY : input.now.getTime() - input.days * DAY_MS;
  for (const { op, crew, haulScu } of input.ops) {
    if (opTime(op) < since) continue;
    for (const { user, guest, source } of crew) {
      if (!rowAttendance(op, source, input.now).showed) continue;
      const entry = row(user, guest);
      if (entry === null) continue;
      entry.ops += 1;
      entry.haul += haulScu;
    }
  }
  for (const donor of input.donors) {
    const entry = row(donor.userId, donor.userId === null ? donor.name : null);
    if (entry === null) continue;
    entry.donations += donor.count;
    entry.donatedScu += donor.scu;
    entry.donatedAuec += donor.auec;
    entry.hidden = entry.hidden || donor.hidden;
  }
  for (const deposit of input.deposits) {
    const entry = row(deposit.userId, deposit.name);
    if (entry === null) continue;
    entry.donations += deposit.count;
    entry.donatedAuec += deposit.amount;
  }
  for (const run of input.runs) {
    const entry = row(run.refiner, null);
    if (entry === null) continue;
    entry.runs += run.count;
    entry.refinedScu += run.scu;
  }
  return [...rows.values()]
    .map((entry) => ({ ...entry, hidden: entry.hidden || (entry.user !== null && input.hidden?.has(entry.user) === true), total: entry.ops + entry.donations + entry.runs }))
    .filter((entry) => entry.total > 0)
    .sort((a, b) => b.total - a.total || b.haul - a.haul || b.donatedScu - a.donatedScu || a.name.localeCompare(b.name));
}

export const PeopleFilter = z.enum(['recent', 'regulars', 'profiles', 'all']);
export type PeopleFilter = z.infer<typeof PeopleFilter>;

export interface PersonFacts {
  ops30: number;
  signed30: number;
  donated30: number;
  hasProfile: boolean;
}

/** R14's filters. */
export function matchesPeople(person: PersonFacts, filter: PeopleFilter): boolean {
  if (filter === 'recent') return person.ops30 > 0 || person.signed30 > 0 || person.donated30 > 0;
  if (filter === 'regulars') return person.ops30 >= 3;
  return filter === 'profiles' ? person.hasProfile : true;
}

/** R14's order: last seen, then ops in 30 days, then name. */
export function peopleOrder(a: { seen: string | null; ops30: number; name: string }, b: { seen: string | null; ops30: number; name: string }): number {
  return (b.seen ?? '').localeCompare(a.seen ?? '') || b.ops30 - a.ops30 || a.name.localeCompare(b.name);
}
